import type { LeaderboardEntry, OwnLeaderboardEntry } from '../ui/Leaderboard';

/**
 * Server-side leaderboard (ructec 2026-10-08, Edge Function `spiel-bestenliste`). The portal hands over a Spielausweis in
 * the URL fragment; it is removed from the address bar at once and kept in localStorage ("bau-run.ausweis", valid 30 days
 * on the server, deleted by "Abmelden" – listed in the privacy policy, ructec.com/datenschutz#bauralley), so the player
 * stays signed in across tabs ("wenn ich angemeldet bin, muss das Spiel das sehen"). On load the game asks the server who
 * it is (short name, company, place, best score); every run gets a run id at its start and reports points and metres once
 * at its end – the database checks the Spielausweis, the run id, a rate limit and plausibility. Without one nothing is sent.
 */

const KEY = 'bau-run.ausweis';
/** Random 64-hex Spielausweis (no account ids inside – the database keeps only its hash). */
const AUSWEIS = /^[0-9a-f]{64}$/;

/** The signed-in player as the server reports it at the start of a run. */
export interface LeaderboardPlayer {
  readonly company: string;
  readonly name: string;
}

export interface LeaderboardState {
  readonly entries: readonly LeaderboardEntry[];
  readonly own: OwnLeaderboardEntry | null;
}

type FetchFn = typeof fetch;
type SessionStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function browserStore(): SessionStore | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Takes the Spielausweis out of the URL fragment (#ausweis=…) and clears the fragment; null if there is none. */
export function takeAusweisFromLocation(
  location: { hash: string; pathname: string; search: string } | undefined = globalThis.location,
  history: Pick<History, 'replaceState'> | undefined = globalThis.history,
): string | null {
  const match = /^#ausweis=([^&]+)$/.exec(location?.hash ?? '');
  if (!match) return null;
  history?.replaceState(null, '', `${location!.pathname}${location!.search}`);
  let value: string;
  try {
    value = decodeURIComponent(match[1]!);
  } catch {
    return null;
  }
  return AUSWEIS.test(value) ? value : null;
}

export class LeaderboardService {
  private ausweis: string | null;
  /** Run id of the running run – a promise, because the run may end before the server answered. */
  private run: Promise<string | null> | null = null;
  /** Own list entry id (`ich`) once the server knows the player. */
  private ownId: string | null = null;
  private ownResult: { rank: number; score: number; meters: number | null } | null = null;
  player: LeaderboardPlayer | null = null;

  constructor(
    private readonly url: string | null,
    private readonly store: SessionStore | null = browserStore(),
    private readonly fetchFn: FetchFn = (...args) => globalThis.fetch(...args),
    handedOver: string | null = takeAusweisFromLocation(),
  ) {
    if (handedOver) this.write(handedOver);
    const stored = handedOver ?? this.read();
    this.ausweis = url && stored && AUSWEIS.test(stored) ? stored : null;
  }

  /** True while a Spielausweis is present (signed in for the leaderboard on this host). */
  get signedIn(): boolean {
    return this.ausweis !== null;
  }

  /**
   * On load: who is signed in – short name, company, own place and best score (resolves false and signs out if the
   * Spielausweis is no longer valid; never throws).
   */
  async whoAmI(): Promise<boolean> {
    if (!this.url || !this.ausweis) return false;
    const data = await this.post({ aktion: 'ich', ausweis: this.ausweis });
    if (!data) return this.signedIn;
    if (typeof data.firma === 'string' && typeof data.kurzname === 'string') this.player = { company: data.firma, name: data.kurzname };
    if (typeof data.ich === 'string') this.ownId = data.ich;
    if (typeof data.platz === 'number' && typeof data.bestwert === 'number') {
      this.ownResult = { rank: data.platz, score: data.bestwert, meters: typeof data.meter === 'number' ? data.meter : null };
    }
    return true;
  }

  /** Top list from the server (empty without backend or on errors – the game never waits for it). */
  async list(size: number): Promise<LeaderboardState> {
    if (!this.url) return { entries: [], own: null };
    try {
      const response = await this.fetchFn(`${this.url}?liste=${size}`);
      const data = (await response.json()) as { eintraege?: Array<Record<string, unknown>> };
      const entries: LeaderboardEntry[] = (data.eintraege ?? [])
        .filter((e) => typeof e.kurzname === 'string' && typeof e.punkte === 'number')
        .map((e) => ({
          id: typeof e.ich === 'string' ? e.ich : undefined,
          name: e.kurzname as string,
          company: typeof e.firma === 'string' ? e.firma : undefined,
          logoUrl: typeof e.logoUrl === 'string' && e.logoUrl.startsWith(new URL(this.url!).origin) ? e.logoUrl : null,
          score: e.punkte as number,
          meters: typeof e.meter === 'number' ? e.meter : null,
        }));
      const index = this.ownId ? entries.findIndex((e) => e.id === this.ownId) : -1;
      const own: OwnLeaderboardEntry | null = index >= 0
        ? { ...entries[index]!, rank: index + 1 }
        : this.ownResult && this.player
          ? { id: this.ownId ?? undefined, name: this.player.name, company: this.player.company, score: this.ownResult.score, meters: this.ownResult.meters, rank: this.ownResult.rank }
          : null;
      return { entries, own };
    } catch {
      return { entries: [], own: null };
    }
  }

  /** A run starts: ask the server for a run id (signed-in players only). */
  startRun(): void {
    if (!this.url || !this.ausweis) return;
    this.run = this.post({ aktion: 'start', ausweis: this.ausweis }).then((data) => {
      if (!data || typeof data.lauf !== 'string') return null;
      if (typeof data.firma === 'string' && typeof data.kurzname === 'string') this.player = { company: data.firma, name: data.kurzname };
      if (typeof data.ich === 'string') this.ownId = data.ich;
      return data.lauf;
    });
  }

  /**
   * The run is over: report its score once. Resolves with the player's place and best score once the server answered,
   * null if nothing was reported or it failed (never throws).
   */
  async endRun(score: number, meters: number): Promise<{ rank: number; score: number; newBest: boolean } | null> {
    const run = this.run;
    this.run = null;
    if (!run || !this.ausweis) return null;
    const id = await run;
    if (!id || !this.ausweis) return null;
    const data = await this.post({
      aktion: 'melden', ausweis: this.ausweis, lauf: id, punkte: Math.max(0, Math.floor(score)), meter: Math.max(0, Math.floor(meters)),
    });
    if (!data) return null;
    if (typeof data.ich === 'string') this.ownId = data.ich;
    if (typeof data.platz !== 'number' || typeof data.bestwert !== 'number') return null;
    const newBest = data.neu === true;
    // metres belong to the best run: new best → this run's metres, otherwise keep the known ones
    this.ownResult = { rank: data.platz, score: data.bestwert, meters: newBest ? Math.floor(meters) : this.ownResult?.meters ?? null };
    return { rank: data.platz, score: data.bestwert, newBest };
  }

  private async post(body: Record<string, unknown>): Promise<Record<string, unknown> | null> {
    try {
      // text/plain keeps it a simple CORS request (no preflight), like the ad report
      const response = await this.fetchFn(this.url!, { method: 'POST', body: JSON.stringify(body), headers: { 'Content-Type': 'text/plain' } });
      if (response.status === 401) {
        this.signOut(); // Spielausweis expired or no longer valid: sign in again on ructec.com
        return null;
      }
      return response.ok ? ((await response.json()) as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }

  /** "Abmelden" in the game (or Spielausweis no longer valid): forget it on this device. */
  signOut(): void {
    this.ausweis = null;
    this.player = null;
    this.ownId = null;
    this.ownResult = null;
    try {
      this.store?.removeItem(KEY);
    } catch {
      // blocked storage – nothing kept anyway
    }
  }

  private read(): string | null {
    try {
      return this.store?.getItem(KEY) ?? null;
    } catch {
      return null;
    }
  }

  private write(value: string): void {
    try {
      this.store?.setItem(KEY, value);
    } catch {
      // blocked storage: signed in for this page view only
    }
  }
}
